import { useLayoutEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import type { Municipality, RealmInfo } from '../../../api/types';
import { Badge, Button, Input, Label, Select } from '../../../components/ui';
import { useIsDesktop } from '../../../hooks/useMediaQuery';
import {
  defaultUtility, emptyGateway, emptyMeter, emptyPocket, emptyRoom, emptySite, emptyUser, newMeterInputId, parseFactor,
  type FormGateway, type FormMeter, type FormRoom, type FormSite, type OnboardingForm, type Utility,
} from '../../../lib/onboarding';

interface Props {
  value: OnboardingForm;
  onChange: (next: OnboardingForm) => void;
  realms: RealmInfo[];
  realmLocked?: boolean;
  municipalities: Municipality[];
}

const QUICK_ROOMS = ['Electricity', 'Water'];
const UTILITY_LABEL: Record<Utility, string> = { electricity: 'Electricity', water: 'Water' };
// The `statusErrorText` pair (src/lib/theme.ts), which contrast.test.ts polices on all three surfaces.
const ERROR_TEXT = 'text-red-600 dark:text-red-400';

// Serials like CT0000001, typed on phones in the field: no capital-first, no autocorrect, no red squiggles.
const SERIAL_INPUT = { autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false } as const;

const count = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
const meterSerialId = (meterKey: string) => `ob-ms-${meterKey}`;
const gatewaySerialId = (gatewayKey: string) => `ob-serial-${gatewayKey}`;
const roomNameId = (roomKey: string) => `ob-roomname-${roomKey}`;

/** Ask for an element (by id) to be focused once the next render has put it on the page. */
type FocusNext = (id: string) => void;

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  const id = `ob-section-${title.toLowerCase()}`;
  return (
    <section aria-labelledby={id} className="bg-surface-raised rounded-xl border border-border-token">
      <div className="flex items-center justify-between gap-3 px-4 lg:px-6 py-3 border-b border-border-token">
        <h2 id={id} className="text-base font-semibold text-content">{title}</h2>
        {action}
      </div>
      <div className="px-4 lg:px-6 py-4 space-y-4">{children}</div>
    </section>
  );
}

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="mt-1 text-xs text-content-muted">{hint}</p>}
    </div>
  );
}

export default function OnboardForm({ value, onChange, realms, municipalities, realmLocked = false }: Props) {
  const desktop = useIsDesktop();
  const set = (patch: Partial<OnboardingForm>) => onChange({ ...value, ...patch });
  const setSite = (key: string, patch: Partial<FormSite>) =>
    set({ sites: value.sites.map((s) => (s.key === key ? { ...s, ...patch } : s)) });

  // A button that adds a gateway or a room puts the cursor in the new input, which only exists
  // after the next render. (The new-meter row needs no such move: it becomes the meter in place.)
  const pendingFocus = useRef<string | null>(null);
  const focusNext: FocusNext = (id) => { pendingFocus.current = id; };
  useLayoutEffect(() => {
    const id = pendingFocus.current;
    if (!id) return;
    pendingFocus.current = null;
    document.getElementById(id)?.focus();
  });

  // The display name follows the realm until someone types their own: one client's name must
  // never ride along onto another realm's request (it becomes that realm's Keycloak display name).
  const pickRealm = (realm: string) => {
    const info = realms.find((r) => r.realm === realm);
    const previous = realms.find((r) => r.realm === value.realm)?.displayName;
    const typed = value.friendly_name !== '' && value.friendly_name !== previous;
    set({ realm, friendly_name: typed ? value.friendly_name : info?.displayName ?? '' });
  };

  return (
    <div className="space-y-6">
      <Section title="Client">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Field id="ob-realm" label="Realm (created first in the OpenRemote Manager)">
            <Select id="ob-realm" value={value.realm} disabled={realmLocked} onChange={(e) => pickRealm(e.target.value)}>
              <option value="">Pick a realm</option>
              {realms.map((r) => <option key={r.realm} value={r.realm}>{r.displayName} ({r.realm})</option>)}
            </Select>
          </Field>
          <Field id="ob-name" label="Display name">
            <Input id="ob-name" value={value.friendly_name} onChange={(e) => set({ friendly_name: e.target.value })} />
          </Field>
          <Field id="ob-muni" label="Municipality">
            <Select id="ob-muni" value={value.municipality_id} onChange={(e) => set({ municipality_id: e.target.value })}>
              <option value="">Not set</option>
              {municipalities.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </Select>
          </Field>
          <div className="min-w-0 space-y-2">
            <label className="flex items-center gap-2 text-sm text-content">
              <input type="checkbox" checked={value.billing_app} onChange={(e) => set({ billing_app: e.target.checked })} />
              Billing portal login
            </label>
            <label className="flex items-center gap-2 text-sm text-content">
              <input type="checkbox" checked={value.demand_calc} onChange={(e) => set({ demand_calc: e.target.checked })} />
              Electricity demand
            </label>
          </div>
        </div>
      </Section>

      <Section title="Sites" action={
        <Button variant="secondary" size="sm" onClick={() => set({ sites: [...value.sites, emptySite()] })}>+ Add site</Button>
      }>
        {value.sites.length === 0 && <p className="text-sm text-content-muted">No sites yet.</p>}
        {value.sites.map((site, i) => (
          <SiteCard key={site.key} site={site} number={i + 1} desktop={desktop} focusNext={focusNext}
            onChange={(patch) => setSite(site.key, patch)}
            onRemove={() => set({ sites: value.sites.filter((s) => s.key !== site.key) })} />
        ))}
      </Section>

      <Section title="Users" action={
        <Button variant="secondary" size="sm" onClick={() => set({ users: [...value.users, emptyUser()] })}>+ Add user</Button>
      }>
        <p className="text-sm text-content-muted">Every user gets the standard client login. Nothing more can be granted here.</p>
        {value.users.map((u, i) => (
          <div key={u.key} className="flex flex-col lg:flex-row lg:items-end gap-2">
            <div className="min-w-0 flex-1">
              <Field id={`ob-user-${u.key}`} label={`User ${i + 1} email`}>
                <Input id={`ob-user-${u.key}`} type="email" value={u.email}
                  onChange={(e) => set({ users: value.users.map((x, k) => (k === i ? { ...x, email: e.target.value } : x)) })} />
              </Field>
            </div>
            <Button variant="ghost" size="sm" aria-label={`Remove user ${i + 1}`}
              onClick={() => set({ users: value.users.filter((_, k) => k !== i) })}>Remove</Button>
          </div>
        ))}
      </Section>

      <Section title="Pockets" action={
        <Button variant="secondary" size="sm" onClick={() => set({ pockets: [...value.pockets, emptyPocket()] })}>+ Add pocket</Button>
      }>
        {value.pockets.map((p, i) => {
          const hintId = `ob-acct-hint-${p.key}`;
          // One row at lg+ (name, account number, Remove) with the hint in the row below, under its input.
          // Aligning the row on the inputs' bottom edge needs the hint outside the account Field, which
          // would otherwise make that Field taller than the name Field. Below lg everything stacks in
          // source order, so the hint follows its input.
          return (
            <div key={p.key} className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 lg:items-end">
              <div className="min-w-0 lg:col-start-1 lg:row-start-1">
                <Field id={`ob-pocket-${p.key}`} label={`Pocket ${i + 1} name`}>
                  <Input id={`ob-pocket-${p.key}`} value={p.name}
                    onChange={(e) => set({ pockets: value.pockets.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)) })} />
                </Field>
              </div>
              <div className="min-w-0 lg:col-start-2 lg:row-start-1">
                <Field id={`ob-acct-${p.key}`} label="Tenant account number">
                  <Input id={`ob-acct-${p.key}`} value={p.account_number} aria-describedby={hintId}
                    onChange={(e) => set({ pockets: value.pockets.map((x, k) => (k === i ? { ...x, account_number: e.target.value } : x)) })} />
                </Field>
              </div>
              <p id={hintId} className="min-w-0 -mt-1 break-words text-xs text-content-muted lg:col-start-2 lg:row-start-2 lg:self-start">
                For the tenancy you create in Billing &gt; Tenants; it is not stored on the pocket.
              </p>
              <Button variant="ghost" size="sm" aria-label={`Remove pocket ${i + 1}`} className="lg:col-start-3 lg:row-start-1"
                onClick={() => set({ pockets: value.pockets.filter((_, k) => k !== i) })}>Remove</Button>
            </div>
          );
        })}
      </Section>

      <Section title="Notes">
        <Label htmlFor="ob-notes">Anything unusual (shared meters, sub-tenants, meters moving from another client)</Label>
        <textarea id="ob-notes" rows={4} value={value.notes} onChange={(e) => set({ notes: e.target.value })}
          className="w-full rounded-md border border-border-token bg-surface-raised px-3.5 py-2.5 text-sm text-content" />
      </Section>
    </div>
  );
}

/** A site: its rooms are containers, and every gateway lives inside its room. */
function SiteCard({ site, number, desktop, focusNext, onChange, onRemove }: {
  site: FormSite; number: number; desktop: boolean; focusNext: FocusNext;
  onChange: (patch: Partial<FormSite>) => void; onRemove: () => void;
}) {
  const patchGateway = (key: string, patch: Partial<FormGateway>) =>
    onChange({ gateways: site.gateways.map((g) => (g.key === key ? { ...g, ...patch } : g)) });
  const removeGateway = (key: string) => onChange({ gateways: site.gateways.filter((g) => g.key !== key) });
  const addGateway = (room: FormRoom) => {
    const gw = emptyGateway(room.key, defaultUtility(room, site.gateways));
    focusNext(gatewaySerialId(gw.key));
    onChange({ gateways: [...site.gateways, gw] });
  };
  const addRoom = (name: string) => {
    const room = emptyRoom(name);
    if (!name) focusNext(roomNameId(room.key));
    onChange({ rooms: [...site.rooms, room] });
  };

  const roomKeys = new Set(site.rooms.map((r) => r.key));
  const waiting = site.gateways.filter((g) => !roomKeys.has(g.room_key));
  const named = site.rooms.filter((r) => r.name.trim());
  const meters = site.gateways.reduce((n, g) => n + g.meters.length, 0);
  const card = (gw: FormGateway, label: string, roomField?: ReactNode) => (
    <GatewayCard key={gw.key} gateway={gw} number={site.gateways.indexOf(gw) + 1} label={label} roomField={roomField}
      desktop={desktop} onPatch={(patch) => patchGateway(gw.key, patch)} onRemove={() => removeGateway(gw.key)} />
  );

  return (
    <div role="group" aria-label={`Site ${site.building.trim() || number}`} className="rounded-lg border border-border-token p-3 lg:p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="min-w-0 truncate font-semibold text-content">{site.building || `Site ${number}`}</h3>
        <Button variant="ghost" size="sm" aria-label={`Remove site ${number}`} onClick={onRemove}>Remove</Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Field id={`ob-city-${site.key}`} label="City / town">
          <Input id={`ob-city-${site.key}`} value={site.city} onChange={(e) => onChange({ city: e.target.value })} />
        </Field>
        <Field id={`ob-building-${site.key}`} label="Building">
          <Input id={`ob-building-${site.key}`} value={site.building} onChange={(e) => onChange({ building: e.target.value })} />
        </Field>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <p className="min-w-0 text-sm font-semibold text-content">Rooms and their gateways</p>
          <p className="min-w-0 text-xs text-content-muted">
            {`${count(site.rooms.length, 'room')} · ${count(site.gateways.length, 'gateway')} · ${count(meters, 'meter')}`}
          </p>
        </div>

        {waiting.length > 0 && (
          // Gateways from drafts saved before rooms held their gateways, whose room is empty or gone.
          <div role="group" aria-labelledby={`ob-waiting-${site.key}`}
            className="min-w-0 rounded-lg border-2 border-dashed border-border-token bg-surface-sunken p-2 lg:p-3 space-y-3">
            <div className="min-w-0">
              <p id={`ob-waiting-${site.key}`} className="text-sm font-semibold text-content">Not in a room yet</p>
              <p className="text-xs text-content-muted">Pick a room for each of these gateways.</p>
            </div>
            {waiting.map((gw) => card(gw, 'Gateway not in a room yet', (
              <Field id={`ob-room-${gw.key}`} label="Room" hint={gw.former_room.trim() ? `Was in ${gw.former_room.trim()}` : undefined}>
                <Select id={`ob-room-${gw.key}`} value="" onChange={(e) => {
                  // Placing it remounts the card in its room's box: keep the cursor with the gateway.
                  focusNext(gatewaySerialId(gw.key));
                  patchGateway(gw.key, { room_key: e.target.value, former_room: '' });
                }}>
                  <option value="">Pick a room</option>
                  {named.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                </Select>
              </Field>
            )))}
          </div>
        )}

        {site.rooms.map((room, r) => {
          const name = room.name.trim();
          const inRoom = site.gateways.filter((g) => g.room_key === room.key);
          const duplicate = name !== '' && site.rooms.slice(0, r).some((x) => x.name.trim() === name);
          const startsAs = UTILITY_LABEL[defaultUtility(room, site.gateways)];
          const ids = { error: `ob-roomdup-${room.key}`, why: `ob-roomwhy-${room.key}`, hint: `ob-roomhint-${room.key}` };
          return (
            <div key={room.key} role="group" aria-label={`Room ${name || r + 1}`}
              className="min-w-0 rounded-lg border-2 border-border-token bg-surface-sunken p-2 lg:p-3 space-y-3">
              <div className="flex flex-col lg:flex-row lg:items-start gap-2">
                <div className="min-w-0 flex-1 lg:max-w-sm">
                  <label htmlFor={roomNameId(room.key)}
                    className="block mb-1 text-xs font-semibold uppercase tracking-wide text-content-muted">Room</label>
                  <Input id={roomNameId(room.key)} aria-label={`Room ${r + 1} name`} value={room.name}
                    className="font-semibold"
                    aria-invalid={duplicate || undefined} aria-describedby={duplicate ? ids.error : undefined}
                    onChange={(e) => onChange({ rooms: site.rooms.map((x) => (x.key === room.key ? { ...x, name: e.target.value } : x)) })} />
                  {duplicate && (
                    <p id={ids.error} className={clsx('mt-1 text-xs', ERROR_TEXT)}>Another room on this site is already called {name}.</p>
                  )}
                </div>
                <div className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1 lg:pt-5">
                  <Button variant="ghost" size="sm" aria-label={`Remove room ${r + 1}`}
                    disabled={inRoom.length > 0} aria-describedby={inRoom.length > 0 ? ids.why : undefined}
                    onClick={() => onChange({ rooms: site.rooms.filter((x) => x.key !== room.key) })}>Remove room</Button>
                  {inRoom.length > 0 && <span id={ids.why} className="min-w-0 text-xs text-content-muted">Remove its gateways first</span>}
                </div>
              </div>

              {inRoom.map((gw) => card(gw, `Gateway in ${name || 'this room'}`))}

              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Button variant="secondary" size="sm" className="min-w-0 max-w-full [overflow-wrap:anywhere]"
                  disabled={!name} aria-describedby={ids.hint} onClick={() => addGateway(room)}>
                  + Add gateway to {name || 'this room'}
                </Button>
                <span id={ids.hint} className="min-w-0 text-sm text-content-muted">
                  {name ? `(starts as ${startsAs})` : 'Name the room first'}
                </span>
              </div>
            </div>
          );
        })}

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => addRoom('')}>+ Add room</Button>
          {QUICK_ROOMS.filter((q) => !site.rooms.some((r) => r.name.trim() === q)).map((q) => (
            <Button key={q} variant="ghost" size="sm" onClick={() => addRoom(q)}>+ {q}</Button>
          ))}
        </div>
      </div>
    </div>
  );
}

function GatewayCard({ gateway: gw, number, label, roomField, desktop, onPatch, onRemove }: {
  gateway: FormGateway; number: number; label: string; roomField?: ReactNode; desktop: boolean;
  onPatch: (patch: Partial<FormGateway>) => void; onRemove: () => void;
}) {
  const serial = gw.serial.trim();
  const on = serial || `Gateway ${number}`;
  const meters = gw.meters.length;
  return (
    <div role="group" aria-label={`Gateway ${serial || number}`}
      className="min-w-0 rounded-md border border-border-token border-l-4 border-l-accent bg-surface-raised p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-accent break-words">{label}</p>
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="min-w-0 max-w-full truncate text-sm font-semibold text-content">{on}</h4>
            {meters > 0 ? <Badge tone="success">{count(meters, 'meter')}</Badge> : <Badge tone="warning">No meters</Badge>}
          </div>
        </div>
        <Button variant="ghost" size="sm" className="shrink-0" aria-label={`Remove gateway ${serial || number}`} onClick={onRemove}>Remove</Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Field id={`ob-conn-${gw.key}`} label="Connection">
          <Select id={`ob-conn-${gw.key}`} value={gw.connection}
            onChange={(e) => onPatch({ connection: e.target.value as 'modem' | 'pi' })}>
            <option value="modem">Modem</option>
            <option value="pi">Pi gateway</option>
          </Select>
        </Field>
        <Field id={gatewaySerialId(gw.key)} label="Serial"
          hint={gw.connection === 'modem' ? "The modem's serial number" : "The gateway's serial or name"}>
          <Input id={gatewaySerialId(gw.key)} value={gw.serial} {...SERIAL_INPUT} onChange={(e) => onPatch({ serial: e.target.value })} />
        </Field>
        <Field id={`ob-utility-${gw.key}`} label="Utility">
          <Select id={`ob-utility-${gw.key}`} value={gw.utility} onChange={(e) => onPatch({ utility: e.target.value as Utility })}>
            <option value="electricity">Electricity</option>
            <option value="water">Water</option>
          </Select>
        </Field>
        {roomField}
        <Field id={`ob-group-${gw.key}`} label="Group name">
          <Input id={`ob-group-${gw.key}`} value={gw.group} placeholder={gw.serial || 'Same as the serial'}
            onChange={(e) => onPatch({ group: e.target.value })} />
        </Field>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-content">Meters</p>
        <Meters gateway={gw} on={on} desktop={desktop} onPatch={onPatch} />
      </div>
    </div>
  );
}

interface MeterField { label: string; id: string; control: ReactNode; note?: ReactNode }

/** One meter's inputs. Every accessible name carries the meter and its gateway, so no two match. */
function meterFields(m: FormMeter, number: number, on: string, elec: boolean, set: (patch: Partial<FormMeter>) => void): MeterField[] {
  const name = (label: string) => `${label} for meter ${number} on ${on}`;
  const fields: MeterField[] = [
    { label: 'Meter serial', id: meterSerialId(m.key), control: (
      <Input id={meterSerialId(m.key)} aria-label={name('Meter serial')} value={m.serial} autoComplete="off" {...SERIAL_INPUT}
        onChange={(e) => set({ serial: e.target.value })} />
    ) },
    { label: 'Make / model', id: `ob-make-${m.key}`, control: (
      <Input id={`ob-make-${m.key}`} aria-label={name('Make / model')} value={m.make} onChange={(e) => set({ make: e.target.value })} />
    ) },
  ];
  if (!elec) return fields;
  const factor = parseFactor(m.factor_text);
  const noteId = `ob-factornote-${m.key}`;
  fields.push(
    { label: 'Breaker (A)', id: `ob-amps-${m.key}`, control: (
      <Input id={`ob-amps-${m.key}`} aria-label={name('Breaker (A)')} inputMode="decimal" value={m.breaker_size_amps}
        onChange={(e) => set({ breaker_size_amps: e.target.value })} />
    ) },
    { label: 'Phases', id: `ob-ph-${m.key}`, control: (
      <Select id={`ob-ph-${m.key}`} aria-label={name('Phases')} value={m.breaker_phases}
        onChange={(e) => set({ breaker_phases: e.target.value as '' | '1' | '3' })}>
        <option value="">Not set</option>
        <option value="1">1</option>
        <option value="3">3</option>
      </Select>
    ) },
    { label: 'CT factor', id: `ob-factor-${m.key}`, control: (
      <Input id={`ob-factor-${m.key}`} aria-label={name('CT factor')} aria-describedby={noteId} value={m.factor_text}
        placeholder="300 or 1500/5" onChange={(e) => set({ factor_text: e.target.value })} />
    ), note: (
      <p id={noteId} className="mt-1 text-xs text-content-muted" role={factor.ok ? undefined : 'alert'}>
        {factor.ok ? (factor.value === null ? 'Factor empty: 1 (no CTs)' : `Factor = ${factor.value}`) : `Check the factor: ${factor.error}`}
      </p>
    ) },
  );
  return fields;
}

/** A gateway's meters: a table at lg and up, the cards below it, and at both an empty "new meter" row
 *  last. The row is keyed as the meter it will become, so the first character typed turns it into that
 *  meter in place: React keeps the same <input>, and focus, caret and any IME composition carry on
 *  untouched (moving focus to a new input could cancel or double a Gboard or iOS composition). */
function Meters({ gateway: gw, on, desktop, onPatch }: {
  gateway: FormGateway; on: string; desktop: boolean; onPatch: (patch: Partial<FormGateway>) => void;
}) {
  const elec = gw.utility === 'electricity';
  const [pending, setPending] = useState(emptyMeter);
  const setMeter = (key: string, patch: Partial<FormMeter>) =>
    onPatch({ meters: gw.meters.map((m) => (m.key === key ? { ...m, ...patch } : m)) });
  const removeMeter = (key: string) => onPatch({ meters: gw.meters.filter((m) => m.key !== key) });
  const removeName = (m: FormMeter, k: number) => `Remove meter ${m.serial.trim() || k + 1} on ${on}`;
  const fields = (m: FormMeter, k: number) => meterFields(m, k + 1, on, elec, (patch) => setMeter(m.key, patch));

  const newLabel = `New meter serial on ${on}`;
  const startMeter = (e: ChangeEvent<HTMLInputElement>) => {
    const typed = e.target.value;
    if (!typed.trim()) return;
    onPatch({ meters: [...gw.meters, { ...pending, serial: typed }] });
    setPending(emptyMeter());
  };
  // Same element type and position as the meter's serial Input in meterFields, so React reuses it.
  const pendingInput = (labelled: boolean) => (
    <Input id={newMeterInputId(gw.key)} aria-label={labelled ? undefined : newLabel} value="" autoComplete="off" {...SERIAL_INPUT}
      placeholder="Type a meter serial to add a meter" onChange={startMeter} />
  );

  if (!desktop) {
    return (
      // Flat keyed lists at every level from here to the <input> (the cards, and each card's fields):
      // a nested array is a fragment of its own, so a key inside it never matches a sibling outside.
      <div className="space-y-2">
        {[...gw.meters.map((m, k) => (
          <div key={m.key} className="rounded-md bg-surface-sunken p-3 space-y-2">
            {[...fields(m, k).map((f) => (
              <div key={f.id} className="min-w-0">
                <Label htmlFor={f.id}>{f.label}</Label>
                {f.control}
                {f.note}
              </div>
            )), (
              <div key="remove" className="flex justify-end">
                <Button variant="ghost" size="sm" aria-label={removeName(m, k)} onClick={() => removeMeter(m.key)}>Remove</Button>
              </div>
            )]}
          </div>
        )), (
          <div key={pending.key} className="rounded-md border-2 border-dashed border-accent p-3">
            <div key={meterSerialId(pending.key)} className="min-w-0">
              <label htmlFor={newMeterInputId(gw.key)} className="block mb-1.5 text-xs font-semibold text-accent break-words">{newLabel}</label>
              {pendingInput(true)}
            </div>
          </div>
        )]}
      </div>
    );
  }

  const columns = elec ? ['Meter serial', 'Make / model', 'Breaker (A)', 'Phases', 'CT factor'] : ['Meter serial', 'Make / model'];
  return (
    // At the narrowest desktop widths the electricity table is wider than its card, so it scrolls
    // inside its own border instead of squeezing the serial inputs into slivers.
    <div className="overflow-x-auto rounded-md border border-border-token">
      <table className={clsx('w-full table-fixed text-sm', elec && 'min-w-[44rem]')}>
        <caption className="sr-only">Meters on {on}</caption>
        <colgroup>
          <col />
          <col />
          {elec && <><col className="w-24" /><col className="w-28" /><col className="w-40" /></>}
          <col className="w-14" />
        </colgroup>
        <thead className="bg-surface-sunken text-left text-xs text-content-muted">
          <tr>
            {columns.map((c) => <th key={c} scope="col" className="px-1.5 py-2 font-semibold">{c}</th>)}
            <th scope="col" className="px-1.5 py-2"><span className="sr-only">Remove</span></th>
          </tr>
        </thead>
        <tbody>
          {/* Flat keyed lists down to the <input>, as for the cards above. */}
          {[...gw.meters.map((m, k) => (
            <tr key={m.key} className="border-t border-border-token align-top">
              {[...fields(m, k).map((f) => <td key={f.id} className="p-1.5">{f.control}{f.note}</td>), (
                <td key="remove" className="p-1.5 text-right">
                  <Button variant="ghost" size="sm" aria-label={removeName(m, k)} onClick={() => removeMeter(m.key)}>
                    <span aria-hidden="true">×</span>
                  </Button>
                </td>
              )]}
            </tr>
          )), (
            <tr key={pending.key} className="border-t-2 border-dashed border-accent">
              <td key={meterSerialId(pending.key)} colSpan={columns.length + 1} className="p-1.5">{pendingInput(false)}</td>
            </tr>
          )]}
        </tbody>
      </table>
    </div>
  );
}
